import * as React from 'react';
import { useEffect } from 'react';
import { OnlineStudyPlanContext } from '../Context/OnlineStudyPlanContext';
import { ChoiceEqualsChoice, ParentItemEqualsParentItem } from '../Context/OnlineStudyPlanHelpers';
import { MessageBroker } from '~/Features/Messaging/MessageBroker';
import type { OnlineApplicationChoice } from '~/Features/OnlineApplication/Model';
import { OnlineApplicationProcessState } from '~/Features/OnlineApplication/OnlineApplicationProcessState';
import {
    AddedToBasket,
    RemovedFromBasket,
} from '~/Features/Pages/OnlineStudyPage/Subjects/Basket/Events/SubjectsBasketEvents';
import { OnlineAnalytics } from '~/Partials/GoogleTagManager/Analytics/OnlineAnalytics';
import { redirect } from '~/Partials/Utils/utils';

export const useSubjectsBasketModel = () => {
    const context = React.useContext(OnlineStudyPlanContext);

    const processState = OnlineApplicationProcessState();
    const orderChoicesFromBasket = () => {
        const choices = context.choicesFromBasket;
        const appendingSucceeded = processState.append(choices);

        if (appendingSucceeded) {
            redirect(context.onlineApplicationFormUrl);
        }
    };

    useEffect(() => {
        context.setAddChoicesToBasketHandler(addChoicesToBasket);
        context.setRemoveChoicesFromBasketHandler(removeChoicesFromBasket);
    }, []);

    const addChoicesToBasket = (choices: OnlineApplicationChoice | OnlineApplicationChoice[]) => {
        context.setChoicesFromBasket(prevChoices => {
            const choicesToAdd = ([] as OnlineApplicationChoice[]).concat(choices);
            const currentChoices = [...prevChoices];
            let choiceToAddIndex = -1;
            for (const choiceToAdd of choicesToAdd) {
                choiceToAddIndex++;
                let currentChoiceIndex = -1;
                for (const currentChoice of currentChoices) {
                    currentChoiceIndex++;

                    if (currentChoice.subject.code !== choiceToAdd.subject.code) {
                        continue;
                    }

                    if (
                        currentChoice.parentStudy.timeType !== choiceToAdd.parentStudy.timeType ||
                        ParentItemEqualsParentItem(currentChoice.parentItem, choiceToAdd.parentItem) === false
                    ) {
                        currentChoices.splice(currentChoiceIndex, 1);
                        MessageBroker.publish(new RemovedFromBasket(currentChoices));
                        break;
                    }

                    choicesToAdd.splice(choiceToAddIndex, 1);
                    break;
                }
            }

            MessageBroker.publish(new AddedToBasket(choicesToAdd));
            OnlineAnalytics.addProducts(choicesToAdd);

            return [...currentChoices, ...choicesToAdd];
        });
    };

    const removeChoicesFromBasket = (choices: OnlineApplicationChoice | OnlineApplicationChoice[]) => {
        context.setChoicesFromBasket(prevChoices => {
            const choicesToRemove = ([] as OnlineApplicationChoice[]).concat(choices);
            const currentChoices = [...prevChoices];

            let choiceToRemoveIndex = -1;
            // eslint-disable-next-line no-restricted-syntax
            for (const choiceToRemove of choicesToRemove) {
                choiceToRemoveIndex++;
                let currentChoiceIndex = -1;
                // eslint-disable-next-line no-restricted-syntax
                for (const currentChoice of currentChoices) {
                    currentChoiceIndex++;

                    if (currentChoice.subject.code !== choiceToRemove.subject.code) {
                        if (currentChoiceIndex === currentChoices.length - 1) {
                            choicesToRemove.splice(choiceToRemoveIndex, 1);
                        }

                        // eslint-disable-next-line no-continue
                        continue;
                    }

                    if (ChoiceEqualsChoice(currentChoice, choiceToRemove)) {
                        currentChoices.splice(currentChoiceIndex, 1);
                        break;
                    }

                    choicesToRemove.splice(choiceToRemoveIndex, 1);
                    break;
                }
            }

            MessageBroker.publish(new RemovedFromBasket(choicesToRemove));
            OnlineAnalytics.removeChoiceProducts(choicesToRemove);

            return currentChoices;
        });
    };

    return {
        removeChoicesFromBasket,
        addChoicesToBasket,
        orderChoicesFromBasket,
    };
};
